import React from 'react';
import ReactDOM from 'react-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { normalizeMessageContentHtml } from '../../utils/fileUtils';
import { TaskStatusIcon, formatTaskStatusLabel, TASK_STATUS_STYLES } from './attendanceTaskStatus';

const hasMeaningfulHtml = (html) => {
  if (!html || html === '<p><br></p>' || html === '<p></p>') return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim().length > 0;
};

const QUOTES = [
  'Small steps every day build extraordinary years.',
  'Focus on progress, not perfection.',
  'Your future is created by what you do today.',
  'Discipline is choosing what you want most over what you want now.',
  'Start where you are. Use what you have. Do what you can.',
  'The secret of getting ahead is getting started.',
];

/** Shared palette — chart, legend, and column headers use the same hues */
const CATEGORIES = {
  reminders: {
    key: 'reminders',
    label: 'Reminders',
    color: '#EA580C',
    colorLight: '#FB923C',
    header: 'bg-orange-100 dark:bg-orange-950/50 text-orange-900 dark:text-orange-100 border-orange-200 dark:border-orange-800',
    itemBorder: 'border-l-[3px] border-l-orange-500',
    dot: 'bg-orange-500',
  },
  tasks: {
    key: 'tasks',
    label: 'Pending Tasks',
    color: '#2563EB',
    colorLight: '#60A5FA',
    header: 'bg-blue-100 dark:bg-blue-950/50 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800',
    itemBorder: 'border-l-[3px] border-l-blue-500',
    dot: 'bg-blue-500',
  },
  workflows: {
    key: 'workflows',
    label: 'Workflows Assigned',
    color: '#9333EA',
    colorLight: '#C084FC',
    header: 'bg-purple-100 dark:bg-purple-950/50 text-purple-900 dark:text-purple-100 border-purple-200 dark:border-purple-800',
    itemBorder: 'border-l-[3px] border-l-purple-500',
    dot: 'bg-purple-500',
  },
  calls: {
    key: 'calls',
    label: 'Today Calls',
    color: '#059669',
    colorLight: '#34D399',
    header: 'bg-teal-100 dark:bg-teal-950/50 text-teal-900 dark:text-teal-100 border-teal-200 dark:border-teal-800',
    itemBorder: 'border-l-[3px] border-l-emerald-500',
    dot: 'bg-emerald-500',
  },
};

const dayTypeButton = {
  Hectic: {
    label: "It's a hectic day today! Let's Go",
    className: 'bg-rose-600 hover:bg-rose-700',
  },
  'Normal Day': {
    label: "It's a normal day today! Let's Go",
    className: 'bg-amber-600 hover:bg-amber-700',
  },
  'Chilled out Day': {
    label: "It's a chilled out day today! Let's Go",
    className: 'bg-emerald-600 hover:bg-emerald-700',
  },
};

const sumMinutes = (items = []) =>
  items.reduce((sum, item) => sum + (Number(item?.minutes) > 0 ? Number(item.minutes) : 0), 0);

const Column = ({ theme, items, emptyLabel, minutes, count }) => (
  <section className="min-w-0 flex flex-col rounded-xl border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm">
    <div className={`px-3 py-2.5 border-b ${theme.header}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${theme.dot}`} aria-hidden />
          <h3 className="text-[11px] font-bold uppercase tracking-wider truncate">{theme.label}</h3>
        </div>
        <span className="flex-shrink-0 text-[10px] font-bold tabular-nums opacity-90">
          {count}
          {minutes > 0 ? ` · ${minutes}m` : ''}
        </span>
      </div>
    </div>
    <div className="p-2 space-y-1.5 max-h-56 overflow-y-auto">
      {!items?.length ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic px-1 py-2">{emptyLabel}</p>
      ) : (
        items.map((item) => (
          <div
            key={item.id || item.title}
            className={`rounded-lg border border-gray-100 dark:border-gray-700/70 bg-gray-50/90 dark:bg-gray-800/70 px-2.5 py-2 ${theme.itemBorder}`}
          >
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-snug">
              {item.title}
            </p>
            <div className="flex items-center justify-between gap-1 mt-1">
              {item.subtitle ? (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{item.subtitle}</p>
              ) : <span />}
              {item.minutes != null && item.minutes > 0 && (
                <span
                  className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums"
                  style={{ color: theme.color, backgroundColor: `${theme.color}18` }}
                >
                  {item.minutes}m
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  </section>
);

const MinutesDoughnut = ({ segments, totalMinutes }) => {
  const active = segments.filter((s) => s.value > 0);
  const pieData = active.length > 0
    ? active
    : [{ key: 'empty', name: 'No time planned', value: 1, color: '#CBD5E1', pct: 0 }];

  return (
    <div className="rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 px-3 py-2.5 shadow-inner">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/80 mb-2 text-center sm:text-left">
        Work load today
      </p>
      <div className="flex items-center gap-4">
        <div className="relative w-[96px] h-[96px] flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={28}
                outerRadius={44}
                stroke="#ffffff"
                strokeWidth={2}
                paddingAngle={active.length > 1 ? 3 : 0}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.key || entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-lg font-bold text-white leading-none tabular-nums">{totalMinutes}</span>
            <span className="text-[9px] text-white/85 font-semibold uppercase tracking-wide">minutes</span>
          </div>
        </div>

        <ul className="space-y-1.5 min-w-[130px]">
          {segments.map((row) => (
            <li
              key={row.key}
              className={`flex items-center gap-2 text-[11px] rounded-lg px-2 py-1 ${
                row.value > 0 ? 'bg-white/20 text-white' : 'bg-white/5 text-white/50'
              }`}
            >
              <span
                className="w-3 h-3 rounded-sm flex-shrink-0 shadow-sm ring-1 ring-white/30"
                style={{ backgroundColor: row.value > 0 ? row.color : '#94A3B8' }}
                aria-hidden
              />
              <span className="truncate font-medium flex-1">{row.shortLabel}</span>
              <span className="font-bold tabular-nums whitespace-nowrap">
                {row.value > 0 ? `${row.value}m` : '—'}
                {row.value > 0 && totalMinutes > 0 && (
                  <span className="font-normal opacity-80 ml-0.5">({row.pct}%)</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const COMPLETED_TASKS_THEME = {
  label: 'Completed Tasks',
  color: '#059669',
  header: 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-900 dark:text-emerald-100 border-emerald-200 dark:border-emerald-800',
  itemBorder: 'border-l-[3px] border-l-emerald-500',
  dot: 'bg-emerald-500',
};

const COMMENTS_THEME = {
  label: 'Comments',
  header: 'bg-rose-100 dark:bg-rose-950/50 text-rose-900 dark:text-rose-100 border-rose-200 dark:border-rose-800',
  dot: 'bg-rose-500',
};

const CompletedTasksSection = ({ tasks = [], className = '' }) => {
  const completed = tasks.map((task) => ({ ...task, status: task.status || 'COMPLETED' }));
  const totalMinutes = completed.reduce(
    (sum, task) => sum + (Number(task.minutes) > 0 ? Number(task.minutes) : 0),
    0,
  );

  return (
    <section className={`rounded-xl border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm ${className}`}>
      <div className={`px-3 py-2.5 border-b ${COMPLETED_TASKS_THEME.header}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COMPLETED_TASKS_THEME.dot}`} aria-hidden />
            <h3 className="text-[11px] font-bold uppercase tracking-wider truncate">{COMPLETED_TASKS_THEME.label}</h3>
          </div>
          <span className="flex-shrink-0 text-[10px] font-bold tabular-nums opacity-90">
            {completed.length}
            {totalMinutes > 0 ? ` · ${totalMinutes}m` : ''}
          </span>
        </div>
      </div>
      <div className="p-2 space-y-1.5 max-h-56 overflow-y-auto">
        {!completed.length ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic px-1 py-2">
            No tasks completed
          </p>
        ) : (
          completed.map((task) => {
            const status = task.status || 'COMPLETED';
            const statusStyle = TASK_STATUS_STYLES[status] || TASK_STATUS_STYLES.COMPLETED;
            return (
              <div
                key={task.id || task.title}
                className={`rounded-lg border border-gray-100 dark:border-gray-700/70 bg-gray-50/90 dark:bg-gray-800/70 px-2.5 py-2 ${COMPLETED_TASKS_THEME.itemBorder}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full ${statusStyle}`}>
                    <TaskStatusIcon status={status} className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                      {task.title}
                    </p>
                    <div className="flex items-center justify-between gap-1 mt-1">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${statusStyle}`}>
                        {formatTaskStatusLabel(status)}
                      </span>
                      {task.minutes != null && task.minutes > 0 && (
                        <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums text-emerald-700 bg-emerald-100 dark:text-emerald-200 dark:bg-emerald-900/50">
                          {task.minutes}m
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};

const CommentsSection = ({ comment }) => {
  const html = normalizeMessageContentHtml(comment || '');
  return (
    <section className="rounded-xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm">
      <div className={`px-3 py-2.5 border-b ${COMMENTS_THEME.header}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COMMENTS_THEME.dot}`} aria-hidden />
          <h3 className="text-[11px] font-bold uppercase tracking-wider truncate">{COMMENTS_THEME.label}</h3>
        </div>
      </div>
      <div className="p-2.5">
        <div
          className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 text-xs leading-relaxed rounded-lg border border-gray-100 dark:border-gray-700/70 bg-gray-50/90 dark:bg-gray-800/70 px-2.5 py-2 min-h-[72px]"
          dangerouslySetInnerHTML={{
            __html: hasMeaningfulHtml(html)
              ? html
              : '<p class="text-gray-400 italic m-0">No comments added</p>',
          }}
        />
      </div>
    </section>
  );
};

const DayStatusHalf = ({ title, accentClass, children }) => (
  <div className="min-w-0 flex flex-col gap-2.5">
    <div className={`flex items-center gap-2 px-1 ${accentClass}`}>
      <h3 className="text-xs font-bold uppercase tracking-wider">{title}</h3>
    </div>
    <div className="space-y-2.5">{children}</div>
  </div>
);

const LogToWorkModal = ({
  briefing,
  userName,
  onClose,
  onConfirm,
  readOnly = false,
  completedTasks,
  comment = '',
  completedMeta = null,
  headerSubtitle,
  dayStats,
  onOpenTimeline,
}) => {
  const [confirming, setConfirming] = React.useState(false);
  const dayType = briefing?.dayType || 'Normal Day';
  const cta = dayTypeButton[dayType] || dayTypeButton['Normal Day'];

  const reminderMins = sumMinutes(briefing?.reminders);
  const taskMins = sumMinutes(briefing?.tasks);
  const workflowMins = sumMinutes(briefing?.workflows);
  const callMins = sumMinutes(briefing?.calls);
  const totalMinutes = briefing?.totalPlannedMinutes || reminderMins + taskMins + workflowMins + callMins;

  const pct = (v) => (totalMinutes > 0 ? Math.round((v / totalMinutes) * 100) : 0);

  const segments = [
    { ...CATEGORIES.reminders, shortLabel: 'Reminders', value: reminderMins, pct: pct(reminderMins) },
    { ...CATEGORIES.tasks, shortLabel: 'Tasks', value: taskMins, pct: pct(taskMins) },
    { ...CATEGORIES.workflows, shortLabel: 'Workflows', value: workflowMins, pct: pct(workflowMins) },
    { ...CATEGORIES.calls, shortLabel: 'Calls', value: callMins, pct: pct(callMins) },
  ];

  const quoteSeed = (briefing?.date || userName || 'day').length;
  const quote = QUOTES[quoteSeed % QUOTES.length];

  const completedReminderCount = completedMeta?.completedRemindersCount ?? 0;
  const completedReminderMins = completedMeta?.totalCompletedReminderMinutes ?? 0;
  const completedReminderItems = completedReminderCount > 0
    ? [{
      id: 'completed-reminders-summary',
      title: `${completedReminderCount} reminder${completedReminderCount === 1 ? '' : 's'} completed`,
      minutes: completedReminderMins > 0 ? completedReminderMins : null,
    }]
    : [];

  const showDayStatusSplit = readOnly && completedTasks !== undefined;

  if (!briefing) return null;

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      if (onConfirm) {
        await onConfirm();
      }
      onClose();
    } catch (err) {
      console.error('Failed to confirm attendance check-in:', err);
    } finally {
      setConfirming(false);
    }
  };

  const pendingColumns = (
    <>
      <Column
        theme={CATEGORIES.tasks}
        items={briefing.tasks}
        emptyLabel="No pending tasks"
        minutes={taskMins}
        count={briefing.tasks?.length || 0}
      />
      <Column
        theme={CATEGORIES.reminders}
        items={briefing.reminders}
        emptyLabel="No active reminders"
        minutes={reminderMins}
        count={briefing.reminders?.length || 0}
      />
      <Column
        theme={CATEGORIES.workflows}
        items={briefing.workflows}
        emptyLabel="No workflows assigned"
        minutes={workflowMins}
        count={briefing.workflows?.length || 0}
      />
      <Column
        theme={CATEGORIES.calls}
        items={briefing.calls}
        emptyLabel="No calls scheduled"
        minutes={callMins}
        count={briefing.calls?.length || 0}
      />
    </>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <style>{`
        @keyframes attendancePopIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes attendanceGreetingShine {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        .attendance-modal-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(16, 185, 129, 0.55) transparent;
        }
        .attendance-modal-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .attendance-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .attendance-modal-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #34d399, #059669);
          border-radius: 999px;
        }
        .attendance-modal-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #6ee7b7, #10b981);
        }
      `}</style>

      <div
        className="relative w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-emerald-200/70 dark:border-emerald-800 bg-white dark:bg-gray-800"
        style={{ animation: 'attendancePopIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both' }}
        role="dialog"
        aria-labelledby="log-to-work-title"
      >
        {/* Single scroll region for the whole popup body */}
        <div className="attendance-modal-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div className="relative overflow-hidden px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white">
            <div
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                background: 'linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.35) 45%, transparent 70%)',
                backgroundSize: '200% 100%',
                animation: 'attendanceGreetingShine 2.8s ease-in-out infinite alternate',
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative z-[1]">
              <p className="text-emerald-50/90 text-xs sm:text-sm font-medium">
                {headerSubtitle
                  || (briefing.alreadyCheckedIn ? "You're logged in for today" : 'Logged to work')}
                {briefing.dayLabel ? ` · ${briefing.dayLabel}` : ''}
                {briefing.dateLabel ? ` · ${briefing.dateLabel}` : ''}
              </p>
              <h2 id="log-to-work-title" className="text-2xl sm:text-3xl font-bold tracking-tight mt-1 pr-8">
                {briefing.greeting || 'Good morning'}, {userName}
              </h2>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
                <blockquote className="text-sm sm:text-base text-emerald-50/95 italic leading-relaxed border-l-[3px] border-orange-300 pl-3 max-w-xl">
                  “{quote}”
                </blockquote>
                <MinutesDoughnut segments={segments} totalMinutes={totalMinutes} />
              </div>

              {dayStats && (
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
                  <div className="rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">
                      {dayStats.logoutTime ? 'Logout Time' : 'Status'}
                    </p>
                    <p className="text-base font-bold mt-1 tabular-nums truncate">
                      {dayStats.logoutTime || 'In progress'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Total Time Spent</p>
                    <p className="text-base font-bold mt-1 tabular-nums truncate">{dayStats.totalTimeSpent || '0m'}</p>
                  </div>
                  <div className="rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Break Time</p>
                    <p className="text-base font-bold mt-1 tabular-nums truncate">{dayStats.totalBreakTime || '0m'}</p>
                  </div>
                  <div className="rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Check-in</p>
                    <p className="text-base font-bold mt-1 tabular-nums truncate">{dayStats.checkInTime || '—'}</p>
                  </div>
                  <div className="rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Productivity</p>
                    <p className="text-base font-bold mt-1 tabular-nums truncate">
                      {dayStats.productivityPercent != null ? `${dayStats.productivityPercent}%` : '—'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-4 sm:px-5 py-4 bg-gradient-to-b from-emerald-50/50 to-white dark:from-gray-900/50 dark:to-gray-800">
            {showDayStatusSplit ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <DayStatusHalf title="Completed" accentClass="text-emerald-700 dark:text-emerald-300">
                  <CompletedTasksSection tasks={completedTasks} />
                  <Column
                    theme={{ ...CATEGORIES.reminders, label: 'Reminders' }}
                    items={completedReminderItems}
                    emptyLabel="No reminders completed"
                    minutes={completedReminderMins}
                    count={completedReminderCount}
                  />
                  <Column
                    theme={{ ...CATEGORIES.workflows, label: 'Workflows' }}
                    items={[]}
                    emptyLabel="No workflows completed"
                    minutes={0}
                    count={0}
                  />
                  <Column
                    theme={{ ...CATEGORIES.calls, label: 'Calls' }}
                    items={[]}
                    emptyLabel="No calls completed"
                    minutes={0}
                    count={0}
                  />
                  <CommentsSection comment={comment} />
                </DayStatusHalf>
                <DayStatusHalf title="Pending" accentClass="text-blue-700 dark:text-blue-300">
                  {pendingColumns}
                </DayStatusHalf>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
                <Column
                  theme={CATEGORIES.reminders}
                  items={briefing.reminders}
                  emptyLabel="No active reminders"
                  minutes={reminderMins}
                  count={briefing.reminders?.length || 0}
                />
                <Column
                  theme={CATEGORIES.tasks}
                  items={briefing.tasks}
                  emptyLabel="No tasks picked for today"
                  minutes={taskMins}
                  count={briefing.tasks?.length || 0}
                />
                <Column
                  theme={CATEGORIES.workflows}
                  items={briefing.workflows}
                  emptyLabel="No workflows assigned"
                  minutes={workflowMins}
                  count={briefing.workflows?.length || 0}
                />
                <Column
                  theme={CATEGORIES.calls}
                  items={briefing.calls}
                  emptyLabel="No calls scheduled today"
                  minutes={callMins}
                  count={briefing.calls?.length || 0}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 px-4 sm:px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          {readOnly ? (
            <div className={`flex gap-2.5 ${onOpenTimeline ? '' : ''}`}>
              {typeof onOpenTimeline === 'function' && (
                <button
                  type="button"
                  onClick={onOpenTimeline}
                  className="flex-1 rounded-xl text-slate-800 dark:text-slate-100 text-sm sm:text-base font-bold py-3 transition-colors shadow-sm bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
                >
                  Day timeline
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className={`${onOpenTimeline ? 'flex-1' : 'w-full'} rounded-xl text-emerald-800 dark:text-emerald-100 text-sm sm:text-base font-bold py-3 transition-colors shadow-sm bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:hover:bg-emerald-900/70`}
              >
                Close
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming}
              className={`w-full rounded-xl text-white text-sm sm:text-base font-bold py-3 transition-colors shadow-sm disabled:opacity-70 ${cta.className}`}
            >
              {confirming ? 'Logging in…' : cta.label}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LogToWorkModal;
